import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Button,
  IconButton,
  RadioGroup,
  RadioGroupItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { FormSectionCard } from '../../event-jobs/event-job-create/FormSectionCard';
import { Plus, Trash2 } from 'lucide-react';
import type {
  LinearConfig,
  AudioTrack,
  SubtitleTrack,
  HaPair,
  CreateLinearJobRequest,
} from '@/api/media/live-linear-jobs-types';
import { CUSTOM_CONFIG_PROTOCOLS } from './use-linear-job-form';
import styles from './LinearJobCreate.module.css';

const AUDIO_CODECS = ['aac', 'mp2'];
const SUBTITLE_CODECS = ['dvb_subtitle', 'teletext'];
const LANGUAGES = [
  'eng', 'chi', 'spa', 'may', 'tha', 'ind', 'fil', 'vie',
  'bur', 'ara', 'kor', 'ita', 'msa', 'zho', 'cmn', 'yue',
];

interface LinearConfigSectionProps {
  form: CreateLinearJobRequest;
  onChange: (config: LinearConfig) => void;
  errors: Record<string, string>;
  clearError: (field: string) => void;
}

function updateConfigField(config: LinearConfig, key: keyof LinearConfig, value: unknown): LinearConfig {
  return { ...config, [key]: value };
}

/** Advanced linear-specific options: Video/Audio/Subtitle tracks, HA pairs, DAI, multicast, ingested programs. */
export function LinearConfigSection({
  form,
  onChange,
  errors,
  clearError,
}: LinearConfigSectionProps) {
  const { input_protocol: ip, linear_config: config } = form;
  const showCustomConfig = CUSTOM_CONFIG_PROTOCOLS.includes(ip);
  const isMpegtsOrSrt = ['mpegts', 'mpegts_udp_multicast', 'srt'].includes(ip);
  const isRtp = ip === 'rtp';
  const isMulticast = ip === 'mpegts_udp_multicast' || isRtp;
  const canEnableHa = ip === 'rtmp';
  const enableHa = canEnableHa && (config.ha_pairs?.length ?? 0) > 0;

  const set = (key: keyof LinearConfig, value: unknown) => {
    onChange(updateConfigField(config, key, value));
    clearError(`linear_config.${key}`);
  };

  // ─── Audio tracks ─────────────────────────────────────────────────────────
  const audioTracks = config.audio_tracks || [];
  const addAudio = () =>
    set('audio_tracks', [
      ...audioTracks,
      { pid: '', language: 'eng', codec: 'aac', default: audioTracks.length === 0 },
    ]);
  const updateAudio = (idx: number, patch: Partial<AudioTrack>) => {
    const next = audioTracks.map((t, i) => (i === idx ? { ...t, ...patch } : t));
    set('audio_tracks', next);
  };
  const setAudioDefault = (idx: number) =>
    set('audio_tracks', audioTracks.map((t, i) => ({ ...t, default: i === idx })));
  const removeAudio = (idx: number) => {
    let next = audioTracks.filter((_, i) => i !== idx);
    // Copy the object before mutating — elements of next still reference original track objects
    if (audioTracks[idx]?.default && next.length > 0) {
      next = next.map((t, i) => (i === 0 ? { ...t, default: true } : t));
    }
    set('audio_tracks', next);
  };

  // ─── Subtitle tracks ──────────────────────────────────────────────────────
  const subtitleTracks = config.subtitle_tracks || [];
  const addSubtitle = () =>
    set('subtitle_tracks', [...subtitleTracks, { pid: '', language: 'eng', codec: 'dvb_subtitle', page: '' }]);
  const updateSubtitle = (idx: number, patch: Partial<SubtitleTrack>) => {
    set('subtitle_tracks', subtitleTracks.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  };
  const removeSubtitle = (idx: number) =>
    set('subtitle_tracks', subtitleTracks.filter((_, i) => i !== idx));

  // ─── HA pairs ─────────────────────────────────────────────────────────────
  const haPairs = config.ha_pairs || [];
  const handleHaToggle = (checked: boolean) => {
    set('ha_pairs', checked
      ? [{ ingestion_domain: '', origin_domain: '' }, { ingestion_domain: '', origin_domain: '' }]
      : []);
  };
  const updateHaPair = (idx: number, patch: Partial<HaPair>) => {
    const next = [...haPairs];
    next[idx] = { ...next[idx], ...patch };
    set('ha_pairs', next);
  };

  // ─── Ingested programs ────────────────────────────────────────────────────
  const ingestedPrograms = config.ingested_programs || [];
  const addProgram = () =>
    set('ingested_programs', [...ingestedPrograms, { video_pid: '', audio_pid: '' }]);
  const updateProgram = (idx: number, key: 'video_pid' | 'audio_pid', val: string) => {
    set('ingested_programs', ingestedPrograms.map((p, i) => (i === idx ? { ...p, [key]: val } : p)));
  };
  const removeProgram = (idx: number) =>
    set('ingested_programs', ingestedPrograms.filter((_, i) => i !== idx));

  if (!showCustomConfig && !canEnableHa) return null;

  return (
    <>
      {/* The DASH order-bitrate / time-shift flags live in the Basic section
          (extraFlags) — only the ingestion HA settings belong here. */}
      {canEnableHa && (
        <FormSectionCard
          title="Advanced Options"
          description="Redundant ingestion endpoints for high availability."
          actions={
            <label className={styles.switchItem} htmlFor="ha-toggle">
              <span>Enable Ingestion HA</span>
              <Switch checked={enableHa} onCheckedChange={handleHaToggle} id="ha-toggle" />
            </label>
          }
        >
          {enableHa && (
            <>
              {[0, 1].map((idx) => {
                const pair = haPairs[idx] || { ingestion_domain: '', origin_domain: '' };
                return (
                  <div key={idx} className={styles.haPair}>
                    <p className={styles.subLabel}>Stream {idx + 1}</p>
                    <div className={styles.grid2}>
                      <Field label="Ingestion Domain" required>
                        {(fp) => (
                          <Input
                            {...fp}
                            value={pair.ingestion_domain}
                            onChange={(e) => updateHaPair(idx, { ingestion_domain: e.target.value })}
                            placeholder="ingest.example.com"
                          />
                        )}
                      </Field>
                      <Field label="Origin Domain" required>
                        {(fp) => (
                          <Input
                            {...fp}
                            value={pair.origin_domain}
                            onChange={(e) => updateHaPair(idx, { origin_domain: e.target.value })}
                            placeholder="origin.example.com"
                          />
                        )}
                      </Field>
                    </div>
                  </div>
                );
              })}
              {errors['linear_config.ha_pairs'] && (
                <p className={styles.errorText}>{errors['linear_config.ha_pairs']}</p>
              )}
            </>
          )}
        </FormSectionCard>
      )}

      {/* Custom MPEGTS/RTP config */}
      {showCustomConfig && (
        <FormSectionCard title="Input Stream Configuration" description="Track PIDs, multicast settings, and program mapping.">

          {/* Video Track — not for RTP */}
          {isMpegtsOrSrt && (
            <div className={styles.configBlock}>
              <p className={styles.configBlockTitle}>Video Track</p>
              <Field label="PID" required error={errors['linear_config.video_pids']}>
                {(fp) => (
                  <Input
                    {...fp}
                    value={(config.video_pids && config.video_pids.length > 0) ? config.video_pids[0] : ''}
                    onChange={(e) => set('video_pids', e.target.value ? [e.target.value] : [])}
                    placeholder="e.g. v1"
                    wrapperClassName={styles.inputNarrow}
                  />
                )}
              </Field>
            </div>
          )}

          {/* Multicast */}
          {isMulticast && (
            <div className={styles.configBlock}>
              <p className={styles.configBlockTitle}>Multicast Configuration</p>
              <div className={styles.grid2}>
                <Field label="Multicast IP" required error={errors['linear_config.multicast_ip']}>
                  {(fp) => (
                    <Input
                      {...fp}
                      value={config.multicast_ip || ''}
                      onChange={(e) => set('multicast_ip', e.target.value)}
                      placeholder="224.0.0.1"
                    />
                  )}
                </Field>
                <Field label="Multicast Port" required error={errors['linear_config.multicast_port']}>
                  {(fp) => (
                    <Input
                      {...fp}
                      value={config.multicast_port || ''}
                      onChange={(e) => set('multicast_port', e.target.value)}
                      placeholder="5000"
                    />
                  )}
                </Field>
              </div>
            </div>
          )}

          {/* Audio Tracks */}
          {isMpegtsOrSrt && (
            <div className={styles.configBlock}>
              <div className={styles.configBlockHeader}>
                <p className={styles.configBlockTitle}>Audio Tracks</p>
                <Button variant="ghost" size="sm" startIcon={<Plus size={13} />} onClick={addAudio}>
                  Add Track
                </Button>
              </div>
              {errors['linear_config.audio_tracks'] && (
                <p className={styles.errorText}>{errors['linear_config.audio_tracks']}</p>
              )}
              {audioTracks.length === 0 && (
                <p className={styles.emptyText}>No audio tracks added.</p>
              )}
              <RadioGroup
                value={String(audioTracks.findIndex((t) => t.default))}
                onValueChange={(v) => setAudioDefault(Number(v))}
              >
                {audioTracks.map((track, idx) => (
                  <div key={idx} className={styles.trackRow}>
                    <div className={styles.trackFields}>
                      <Field label="PID" required>
                        {(fp) => (
                          <Input
                            {...fp}
                            value={track.pid}
                            onChange={(e) => updateAudio(idx, { pid: e.target.value })}
                            placeholder="a1"
                          />
                        )}
                      </Field>
                      <Field label="Language">
                        {(fp) => (
                          <Select value={track.language} onValueChange={(v) => updateAudio(idx, { language: v })}>
                            <SelectTrigger {...fp}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {LANGUAGES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                      </Field>
                      <Field label="Codec">
                        {(fp) => (
                          <Select value={track.codec} onValueChange={(v) => updateAudio(idx, { codec: v })}>
                            <SelectTrigger {...fp}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {AUDIO_CODECS.map((c) => <SelectItem key={c} value={c}>{c.toUpperCase()}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                      </Field>
                      <label className={styles.defaultTrackLabel}>
                        <RadioGroupItem value={String(idx)} id={`audio-default-${idx}`} />
                        <span>Default</span>
                      </label>
                    </div>
                    {audioTracks.length > 1 && (
                      <IconButton size="sm" aria-label="Remove track" onClick={() => removeAudio(idx)}>
                        <Trash2 size={13} />
                      </IconButton>
                    )}
                  </div>
                ))}
              </RadioGroup>
            </div>
          )}

          {/* Subtitle Tracks */}
          {isMpegtsOrSrt && (
            <div className={styles.configBlock}>
              <div className={styles.configBlockHeader}>
                <p className={styles.configBlockTitle}>Subtitle Tracks</p>
                <Button variant="ghost" size="sm" startIcon={<Plus size={13} />} onClick={addSubtitle}>
                  Add Track
                </Button>
              </div>
              {errors['linear_config.subtitle_tracks'] && (
                <p className={styles.errorText}>{errors['linear_config.subtitle_tracks']}</p>
              )}
              {subtitleTracks.length === 0 && (
                <p className={styles.emptyText}>No subtitle tracks added.</p>
              )}
              {subtitleTracks.map((track, idx) => (
                <div key={idx} className={styles.trackRow}>
                  <div className={styles.trackFields}>
                    <Field label="PID" required>
                      {(fp) => (
                        <Input
                          {...fp}
                          value={track.pid}
                          onChange={(e) => updateSubtitle(idx, { pid: e.target.value })}
                          placeholder="s1"
                        />
                      )}
                    </Field>
                    <Field label="Language">
                      {(fp) => (
                        <Select value={track.language} onValueChange={(v) => updateSubtitle(idx, { language: v })}>
                          <SelectTrigger {...fp}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {LANGUAGES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                    </Field>
                    <Field label="Codec">
                      {(fp) => (
                        <Select value={track.codec} onValueChange={(v) => updateSubtitle(idx, { codec: v })}>
                          <SelectTrigger {...fp}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {SUBTITLE_CODECS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                    </Field>
                    {track.codec === 'teletext' && (
                      <Field label="Page" required>
                        {(fp) => (
                          <Input
                            {...fp}
                            value={track.page || ''}
                            onChange={(e) => updateSubtitle(idx, { page: e.target.value })}
                            placeholder="f1"
                          />
                        )}
                      </Field>
                    )}
                  </div>
                  {subtitleTracks.length > 1 && (
                    <IconButton size="sm" aria-label="Remove track" onClick={() => removeSubtitle(idx)}>
                      <Trash2 size={13} />
                    </IconButton>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Ingested Programs (RTP) */}
          {isRtp && (
            <div className={styles.configBlock}>
              <div className={styles.configBlockHeader}>
                <p className={styles.configBlockTitle}>Ingested Programs</p>
                <Button variant="ghost" size="sm" startIcon={<Plus size={13} />} onClick={addProgram}>
                  Add Program
                </Button>
              </div>
              {errors['linear_config.ingested_programs'] && (
                <p className={styles.errorText}>{errors['linear_config.ingested_programs']}</p>
              )}
              {ingestedPrograms.map((prog, idx) => (
                <div key={idx} className={styles.trackRow}>
                  <div className={styles.trackFields}>
                    <Field label="Video PID" required>
                      {(fp) => (
                        <Input
                          {...fp}
                          value={prog.video_pid}
                          onChange={(e) => updateProgram(idx, 'video_pid', e.target.value)}
                          placeholder="v1"
                        />
                      )}
                    </Field>
                    <Field label="Audio PID" required>
                      {(fp) => (
                        <Input
                          {...fp}
                          value={prog.audio_pid}
                          onChange={(e) => updateProgram(idx, 'audio_pid', e.target.value)}
                          placeholder="a1"
                        />
                      )}
                    </Field>
                  </div>
                  {ingestedPrograms.length > 1 && (
                    <IconButton size="sm" aria-label="Remove program" onClick={() => removeProgram(idx)}>
                      <Trash2 size={13} />
                    </IconButton>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* DAI (MPEGTS only) */}
          {ip === 'mpegts' && (
            <div className={styles.configBlock}>
              <p className={styles.configBlockTitle}>Dynamic Ad Insertion</p>
              <div className={styles.toggleHeader}>
                <Switch
                  checked={config.dai_enabled || false}
                  onCheckedChange={(v) => set('dai_enabled', v)}
                  id="dai-toggle"
                />
                <label htmlFor="dai-toggle" className={styles.toggleLabel}>Enable DAI</label>
              </div>
              {config.dai_enabled && (
                <div className={styles.daiBody}>
                  <Field label="SCTE-35 PIDs" required error={errors['linear_config.scte35_pids']}>
                    {(fp) => (
                      <Input
                        {...fp}
                        value={config.scte35_pids || ''}
                        onChange={(e) => set('scte35_pids', e.target.value)}
                        placeholder="2323"
                        wrapperClassName={styles.inputNarrow}
                      />
                    )}
                  </Field>
                </div>
              )}
            </div>
          )}
        </FormSectionCard>
      )}
    </>
  );
}
